import React, { Suspense, useEffect } from 'react';
import { ACTIONS } from 'src/constants/common';
import { COUNTRY_NAME_BY_CURRENCY_MAP } from 'src/constants/country-name-by-currency-map';
import {
  type LearnMoreType,
  useAppContext,
} from 'src/context/store/app-provider';
import { useAnalytics } from 'src/hooks/use-analytics';
import { useCollectWidgetContext } from 'src/hooks/use-collect-widget-context';
import { useLogSnippetCartRenderedEvent } from 'src/hooks/use-log-snippet-cart-rendered-event';
import { useOnExperimentsReady } from 'src/hooks/use-on-experiments-ready';
import { mobileBridge } from 'src/services/mobile-bridge';
import { SegmentEventType } from 'src/types/segment';
import { getLearnMoreLink } from 'src/utils/get-learn-more-link';
import { isMobileAppContext } from 'src/utils/is-mobile-app-context';
import { setupDatadogForWidgets } from 'src/utils/setup-datadog-for-widgets';

import { Snippet } from './components/snippet';

export const DefaultPromoWidget = () => {
  const {
    installmentsCount,
    formattedCurrency,
    dispatch,
    publicKey,
    merchantCode,
    offers,
    currency,
    priceRaw,
    lang,
  } = useAppContext();
  useOnExperimentsReady(setupDatadogForWidgets);

  const logAnalyticEvent = useAnalytics();
  const widgetContext = useCollectWidgetContext();

  useLogSnippetCartRenderedEvent({
    planSelected: installmentsCount,
    snippetType: [3, 4].includes(installmentsCount)
      ? 'fullInformation'
      : 'notFullInformation',
  });

  const hasOffers = !!offers.installments?.length;

  const openLearnMorePage = () => {
    mobileBridge.openPage(
      getLearnMoreLink({
        price: priceRaw,
        currency: formattedCurrency,
        lang,
        ...(hasOffers && {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          public_key: publicKey,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          merchant_code: merchantCode,
        }),
      }),
      widgetContext
    );
  };

  const openLearnMoreDialog = (type: LearnMoreType) => {
    dispatch({
      type: ACTIONS.SHOW_LEARN_MORE,
      payload: { [type]: true },
    });
  };

  const openLearnMore = (type: LearnMoreType) => {
    const action = isMobileAppContext ? openLearnMorePage : openLearnMoreDialog;

    action(type);

    logAnalyticEvent({
      event: SegmentEventType.LEARN_MORE_CLICKED,
      publicKey: publicKey ? 'yes' : 'no',
      merchantIntegrationType: 'snippetAndPopup',
      planSelected: installmentsCount,
      snippetType: [3, 4].includes(installmentsCount)
        ? 'fullInformation'
        : 'notFullInformation',
      merchantCountry: COUNTRY_NAME_BY_CURRENCY_MAP[currency],
      typeWidget: type,
    });
  };

  useEffect(() => {
    dispatch({
      type: ACTIONS.PREPARE_SCHEDULE_POPUP,
    });
  }, [dispatch]);

  return (
    <Suspense fallback={null}>
      <Snippet onClick={openLearnMore} />
    </Suspense>
  );
};
